import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, isDesktopBreakpoint, isMobileBreakpoint } from '@theme/utilities';

/**
 * Calculate the bottom position of the header component
 * @returns {number} Bottom position in pixels
 */
function getHeaderBottomPosition() {
  const header = document.querySelector('#header-component');
  if (!header) return 0;
  
  const headerRect = header.getBoundingClientRect();
  return headerRect.bottom;
}

/**
 * Update CSS custom properties for menu positioning
 */
function updateMenuPositioning() {
  const headerBottom = getHeaderBottomPosition();
  document.body.style.setProperty('--header-bottom-position', `${headerBottom}px`);
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #wasMobileInThemeEditor = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileTabs();
    this.#setupMobileGroups();

    // Update positioning on scroll for sticky headers
    window.addEventListener('scroll', updateMenuPositioning);
    window.addEventListener('resize', updateMenuPositioning);

    if (window.Shopify?.designMode) {
      this.#wasMobileInThemeEditor = !isDesktopBreakpoint();
      window.addEventListener('resize', this.#onThemeEditorResize);
      document.addEventListener('shopify:block:select', this.#onThemeEditorSelect);
      document.addEventListener('shopify:section:select', this.#onThemeEditorSelect);
    }

    // Initial positioning update
    updateMenuPositioning();
  }

  disconnectedCallback() {
    if (window.Shopify?.designMode) {
      this.#forceReset();
      window.removeEventListener('resize', this.#onThemeEditorResize);
      document.removeEventListener('shopify:block:select', this.#onThemeEditorSelect);
      document.removeEventListener('shopify:section:select', this.#onThemeEditorSelect);
    }

    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('scroll', updateMenuPositioning);
    window.removeEventListener('resize', updateMenuPositioning);
  }

  /**
   * Close and reset drawer state when switching from mobile to desktop in Theme Editor.
   */
  #onThemeEditorResize = () => {
    const isDesktop = isDesktopBreakpoint();

    if (this.#wasMobileInThemeEditor && isDesktop) {
      this.#forceReset();
    }

    this.#wasMobileInThemeEditor = !isDesktop;
  };

  /**
   * In the Theme Editor, keep the drawer open while a header block/section is being edited.
   * Opens the drawer when a block inside it is selected, and only closes it when something
   * outside the header is selected.
   * @param {Event} event
   */
  #onThemeEditorSelect = (event) => {
    if (!(event.target instanceof Node)) return;

    const header = document.querySelector('#header-component');
    const insideHeader = header ? header.contains(event.target) : this.contains(event.target);

    if (!insideHeader) {
      if (this.isOpen) this.#forceReset();
      return;
    }

    if (this.contains(event.target) && isMobileBreakpoint() && !this.isOpen) {
      this.open();
    }
  };

  /**
   * Force reset drawer and cleanup side effects like focus trap and scroll lock.
   */
  #forceReset() {
    const details = this.refs.details;

    if (!details) return;

    reset(details);
    removeTrapFocus();

    const hasAnotherOpenScrollLockElement = Array.from(document.querySelectorAll('[scroll-lock][open]')).some(
      (element) => element !== details
    );

    if (!hasAnotherOpenScrollLockElement) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    const details = this.#getDetailsElement(event);

    return details.hasAttribute('open') ? this.#close(details) : this.open(event);
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.setAttribute('open', '');

    // Update positioning before opening
    updateMenuPositioning();

    // When the header is partially scrolled off-screen (not sticky yet),
    // set inline top on the drawer so it aligns with the visible header bottom.
    // We use inline style because --header-height gets overwritten by ResizeObserver
    // and the CSS "top: unset" override doesn't match in scroll-up mode.
    this.#adjustDrawerPosition(details);

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Adjusts the drawer's position and height so it starts right below the
   * header's actual visible bottom (which can be lower than --header-height
   * alone, e.g. when an announcement bar sits above the header, or when the
   * header is partially scrolled off-screen and not yet sticky).
   *
   * Sets --mobile-drawer-top from the header's real bounding rect (not from
   * the drawer's own rendered position, which would be circular: the CSS
   * open-state rule uses --mobile-drawer-top to set the drawer's `top`, so
   * reading the drawer's rect back would just echo whatever was set before).
   *
   * @param {HTMLDetailsElement} details
   */
  #adjustDrawerPosition(details) {
    const header = document.querySelector('#header-component');
    if (!header) return;

    const drawer = details.querySelector('.menu-drawer');
    if (!drawer || !(drawer instanceof HTMLElement)) return;

    const headerRect = header.getBoundingClientRect();
    const visibleBottom = Math.max(0, headerRect.bottom);

    drawer.style.setProperty('top', `${visibleBottom}px`);
    document.body.style.setProperty('--mobile-drawer-top', `${visibleBottom}px`);
  }

  /**
   * Removes the inline drawer position overrides applied by #adjustDrawerPosition
   * @param {HTMLDetailsElement} details
   */
  #resetDrawerPosition(details) {
    const drawer = details.querySelector('.menu-drawer');
    if (drawer && drawer instanceof HTMLElement) {
      drawer.style.removeProperty('top');
    }
    document.body.style.removeProperty('--mobile-drawer-top');
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      // Remove inline drawer positioning overrides
      this.#resetDrawerPosition(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = /** @type {NodeListOf<HTMLDetailsElement>} */ (
          this.querySelectorAll('details[open]')
        );
        openDetails.forEach((detail) => {
          // Skip resetting accordions that should always stay open (disabled on both mobile and desktop)
          const accordion = detail.closest('accordion-custom');
          if (
            accordion instanceof HTMLElement &&
            accordion.dataset.disableOnMobile === 'true' &&
            accordion.dataset.disableOnDesktop === 'true'
          ) {
            return;
          }
          if (
              accordion instanceof HTMLElement &&
              accordion.hasAttribute('open-by-default-on-mobile') &&
              isMobileBreakpoint()
          ) {
            return;
          }

          reset(detail);
        });
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Setup mobile tabs functionality
   */
  #setupMobileTabs() {
    const tabs = this.querySelectorAll('.mobile-drawer-tab');
    const tabContents = this.querySelectorAll('.mobile-drawer-tab-content');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {

        tabs.forEach(t => {
          t.classList.remove('mobile-drawer-tab--active');
          t.setAttribute('aria-selected', 'false');
        });
        tabContents.forEach(content => {
          content.classList.remove('mobile-drawer-tab-content--active');
        });

        tab.classList.add('mobile-drawer-tab--active');
        tab.setAttribute('aria-selected', 'true');
        
        if (tab instanceof HTMLElement) {
          const tabIndex = tab.dataset.tabIndex;
          const targetContent = this.querySelector(`#tab-content-${tabIndex}`);
          if (targetContent) {
            targetContent.classList.add('mobile-drawer-tab-content--active');
          }
        }
      });
    });
  }

  /**
   * Setup mobile drawer collapsible groups
   */
  #setupMobileGroups() {
    const groupHeaders = this.querySelectorAll('.desktop-drawer-group-header');
    
    groupHeaders.forEach(header => {
      header.addEventListener('click', (e) => {
        e.preventDefault();
        
        const group = header.parentElement;
        if (!group) return;
        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        
        if (!isExpanded) {
          const allGroups = this.querySelectorAll('.desktop-drawer-group');
          const allHeaders = this.querySelectorAll('.desktop-drawer-group-header');
          
            allGroups.forEach(otherGroup => {
              if (otherGroup !== group) {
                otherGroup.classList.remove('desktop-drawer-group--expanded');
                const ul = otherGroup.querySelector('ul');
                if (ul) {
                  ul.style.maxHeight = '';
                }
              }
            });          allHeaders.forEach(otherHeader => {
            if (otherHeader !== header) {
              otherHeader.setAttribute('aria-expanded', 'false');
            }
          });
        }
        
        header.setAttribute('aria-expanded', (!isExpanded).toString());
        group.classList.toggle('desktop-drawer-group--expanded', !isExpanded);
        const ul = group.querySelector('ul');
        if (ul && !isExpanded) {
          ul.style.maxHeight = ul.scrollHeight + 'px';
        }
      });
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
